/* 0.48.15 - Information cards on one line share one height.
 *
 * A card with "Same height as the cards beside it" (on by default) is
 * registered here. After any change in size (its content, the window,
 * a font that loads), the registered cards are measured at their own
 * height and grouped by line: cards whose tops are within 2px of each
 * other and that overlap no other card of the group horizontally. Each
 * card of a group of two or more gets the group's tallest height as
 * its minimum height. Only Information cards take part, so a card next
 * to a long text or a form keeps its own height.
 *
 * Measuring resets the minimum heights and sets them again in the same
 * frame, so the cards' final sizes do not change unless the content
 * did, and the size watcher does not fire again.
 *
 * 0.49.45 - The lines of the cards' heads are lined up first: the
 * title, the main description and the icon's description of each card
 * on a line take the tallest one's height, so a one-line title gets
 * the space under it (and the next parts start level across the
 * cards) instead of all the difference landing at the card's foot.
 * Parts inside a Details opening part are not lined up.
 *
 * 0.49.35 - A card with Details is measured CLOSED (its height less
 * its open part, [data-details-fold]), so opening one card does not
 * make the others on its line taller. The open card is the line's
 * height plus its open part. */

const cards = new Set();
let observer = null;
let frame = 0;

function schedule() {
  if (frame || typeof window === 'undefined') return;
  frame = window.requestAnimationFrame(() => { frame = 0; layout(); });
}

/* 0.49.46 - Also each section line's label and value, by place: the
   1st line shown on each card, the 2nd... (sec1_label, sec1_value...). */
const HEAD_PARTS = ['title', 'id_text', 'icon_text'];
const allParts = (el) => [...el.querySelectorAll('[data-align-part]')]
  .filter(p => !p.closest('[data-details-fold]'));
const partsOf = (el, key) => allParts(el).filter(p => p.getAttribute('data-align-part') === key);
const keysOf = (els) => {
  const seen = new Set(HEAD_PARTS);
  for (const el of els) for (const p of allParts(el)) seen.add(p.getAttribute('data-align-part'));
  // Top to bottom: the head, then the lines in their order.
  const order = (k) => {
    const m = /^sec(\d+)_(label|value)$/.exec(k);
    return m ? 100 + Number(m[1]) * 2 + (m[2] === 'value' ? 1 : 0) : HEAD_PARTS.indexOf(k);
  };
  return [...seen].sort((a, b) => order(a) - order(b));
};

function lineUp(boxes) {
  const lines = [];
  for (const b of boxes) {
    const line = lines.find(l => Math.abs(l.top - b.r.top) <= 2
      && !l.items.some(o => b.r.left < o.r.right - 1 && o.r.left < b.r.right - 1));
    if (line) line.items.push(b); else lines.push({ top: b.r.top, items: [b] });
  }
  return lines;
}

function layout() {
  const list = [...cards].filter(el => el.isConnected);
  if (!list.length) return;
  for (const el of list) {
    el.style.minHeight = '';
    for (const p of el.querySelectorAll('[data-align-part]')) p.style.minHeight = '';
    for (const a of el.querySelectorAll('[data-details-action]')) a.style.marginTop = '';
  }
  // 0.49.45 - The head's parts first, line by line.
  const first = list
    .map(el => ({ el, r: el.getBoundingClientRect() }))
    .filter(b => b.r.height > 0 && b.r.width > 0)
    .sort((a, b) => a.r.top - b.r.top || a.r.left - b.r.left);
  for (const l of lineUp(first)) {
    if (l.items.length < 2) continue;
    for (const key of keysOf(l.items.map(b => b.el))) {
      const found = l.items.map(b => partsOf(b.el, key)[0]).filter(Boolean);
      if (found.length < 2) continue;
      const hs = found.map(p => p.getBoundingClientRect().height);
      const h = Math.max(...hs);
      found.forEach((p, i) => { if (hs[i] < h - 0.5) p.style.minHeight = `${Math.ceil(h)}px`; });
    }
  }
  const boxes = list
    .map((el) => {
      const r = el.getBoundingClientRect();
      const fold = el.querySelector('[data-details-fold]');
      const extra = fold ? fold.getBoundingClientRect().height : 0;
      // 0.49.48 - Open (or opening): its open part at full height.
      const open = !!fold && fold.getAttribute('data-open') === 'true';
      const full = open && fold.firstElementChild ? fold.firstElementChild.scrollHeight : 0;
      return { el, r, base: r.height - extra, extra, open, full };
    })
    .filter(b => b.r.height > 0 && b.r.width > 0)
    .sort((a, b) => a.r.top - b.r.top || a.r.left - b.r.left);
  for (const l of lineUp(boxes)) {
    if (l.items.length < 2) continue;
    const h = Math.max(...l.items.map(b => b.base));
    /* 0.49.48 - The open cards of a line are as tall as each other: the
       line's closed height plus the tallest open part. A shorter one
       puts the difference above its button (the button that shows only
       while open sits in the open part), or at its foot with no button. */
    const openMax = Math.max(0, ...l.items.filter(b => b.open).map(b => b.full));
    for (const b of l.items) {
      const want = h + (b.open ? openMax : b.extra);
      const deficit = want - b.r.height;
      if (deficit < 0.5) continue;
      const act = b.open ? b.el.querySelector('[data-details-fold] [data-details-action]') : null;
      if (act && b.extra >= b.full - 0.5) {
        act.style.marginTop = `${16 + Math.ceil(deficit)}px`;
      } else {
        b.el.style.minHeight = `${Math.ceil(want)}px`;
      }
    }
  }
}

/* 0.49.47 - Work the heights out again now (a card opened or closed). */
export function refreshCardHeights() { schedule(); }

export function watchCardHeight(el) {
  if (!el || typeof window === 'undefined') return () => {};
  cards.add(el);
  if (!observer && 'ResizeObserver' in window) {
    observer = new ResizeObserver(schedule);
    window.addEventListener('resize', schedule);
  }
  /* 0.49.47 - Its content is watched too, not only the card: a card
     held up by its minimum height does not change size when its content
     gets smaller (a card's Details closing), so the card alone never
     told that the heights had to be worked out again, and the old,
     taller height stayed as empty space. */
  const inner = [...el.children];
  if (observer) { observer.observe(el); inner.forEach(c => observer.observe(c)); }
  schedule();
  return () => {
    if (observer) inner.forEach(c => observer.unobserve(c));
    cards.delete(el);
    el.style.minHeight = '';
    for (const p of el.querySelectorAll('[data-align-part]')) p.style.minHeight = '';
    if (observer) observer.unobserve(el);
    schedule();
  };
}
